import { History, LocationState } from 'history';
import { reaction, toJS } from 'mobx';
import { Instance, addDisposer, types } from 'mobx-state-tree';
import history from '../../../router/history';
import getAppRoot from '../utils/getAppRoot';
import validateQueryParams, { pageQueryParams, QueryParamsToReplace } from './utils/validateQueryParams';
import { Pages, SchedulingQueryParams } from './constants';
import { routesPath } from '../../../router/paths';
import { navigateToRouteResourceParams } from './utils/navigateToRouteResouceParams';
import { TopMenuView } from '../../../components/scheduling/ViewMenu';
import { DateTime } from 'luxon';
import getSelectedView, { resolveStoredScheduledView } from '../../../helpers/getSelectedView';

export interface NavigateToResourceOptions {
  deselectRoute?: boolean;
  deselectStop?: boolean;
  deselectOrder?: boolean;
  keepRouteSelected?: boolean;
  targetPage?: Pages;
}

export interface NavigateToResource {
  routeUuid?: string | null;
  stopUuid?: string | null;
  orderUuid?: string | null;
}

const onWorkspaceIdParamChange = (self: any) => {
  addDisposer(self, reaction(
    () => self.params?.workspaceId,
    async (workspaceId: string) => {
      if (workspaceId) {
        // Clear the previous workspace's data (visits, routes, drivers, templates, …)
        // on any workspace change, so figures derived from the store — e.g. the
        // nav's unscheduled-orders badge, or the route-template count that gates the
        // first-run setup modal's default-template seeding — don't linger from the
        // workspace we just left.
        //
        // This reaction only fires when `workspaceId` actually changes (mobx's
        // default comparer), so we reset on every fire, INCLUDING the first entry of
        // the session (`undefined -> workspaceId`). That first-entry case matters:
        // arriving from the workspace list into a freshly-created workspace carries
        // no previous param, yet the normalized store can still hold another
        // workspace's cached DTOs; when the store is genuinely empty the reset is a
        // harmless no-op.
        //
        // Note: we intentionally do NOT gate on `ui.selectedWorkspace` (the old
        // guard) — that reflects the destination workspace, which is undefined until
        // its project loads, so switching to a not-yet-loaded workspace would skip
        // the reset entirely.
        getAppRoot(self).model.resetModelForProject();
        try {
          await getAppRoot(self).workspace.fetchWorkspaceById(workspaceId);
        } catch (e) {
          localStorage.removeItem('selectedWorkspaceId');
        }
      }
    },
  ));

  return {};
};

const sortParams = (currentAppView: Pages, params: QueryParamsToReplace) => {
  const sortedParams = new URLSearchParams();

  const currentPageQueryParams = pageQueryParams[currentAppView];
  if (currentPageQueryParams) {
    Object.keys(currentPageQueryParams).forEach((param) => {
      const paramValue = params?.get(param);
      if (paramValue) {
        sortedParams.set(param, paramValue);
      }
    });
  }

  return sortedParams;
};

const onValidateParams = (self: any) => {
  addDisposer(self, reaction(
    () => toJS({ projectId: getAppRoot(self).ui.selectedWorkspace?.id, urlParams: self.params, queryParams: self.queryParams }),
    // Debounce ensures that we can query additional data prior to revalidating
    ({ projectId, urlParams, queryParams }) => {
      if (self.isWorkspaceMain && !projectId) {
        return;
      }

      // Skip validation validation entirely if page is invalid
      const page = self.currentAppView;
      if (page === Pages.NOT_FOUND) {
        return;
      }

      validateQueryParams({
        routerStore: self, page, urlParams, queryParams,
      });
    }, { delay: 100 },
  ));
  return {};
};

const Router = types
  .model('Router', {
    location: types.optional(types.frozen(), {}),
    action: types.optional(types.string, ''),
  })
  .volatile(() => ({
    history,
    params: {} as { [K: string]: string | undefined },
  }))
  .actions((self: any) => ({
    updateLocation(newState: LocationState) {
      self.location = newState;
      self.action = self.history.action;
    },
    setParams(newParams: any) {
      self.params = newParams;
    },
    setHistory(initialHistory: History) {
      self.history = initialHistory;
    },
    push({ ...props }: any) {
      self.history.push(props);
    },
    replace({ ...props }: any) {
      self.history.replace(props);
    },
    go(n: number) {
      self.history.go(n);
    },
    goBack() {
      self.history.goBack();
    },
    goForward() {
      self.history.goForward();
    },
    reset() {
      self.history.replace('', {});
    },
    navigateToRouteResource(resource: NavigateToResource, options?: NavigateToResourceOptions) {
      const { routeUuid } = resource;
      const { targetPage } = options || {};

      const currentPage = self.currentPage;
      const currentParams = self.queryParams;
      const targetRoute = routeUuid ? getAppRoot(self).model.routeByUuid(routeUuid) : undefined;

      const queryParams = navigateToRouteResourceParams(currentParams, resource, options, targetRoute);

      if (targetPage && targetPage !== currentPage) {
        self.push({
          pathname: targetPage,
          search: new URLSearchParams(queryParams).toString(),
        });

        return;
      }

      self.replaceQueryParams({ ...queryParams });
    },
  }))
  .views((self) => ({
    get isScreenLoading(): boolean {
      const appRoot = getAppRoot(self);
      if (this.isWorkspaceMain) {
        return appRoot.workspace.isWorkspaceFetching;
      }

      return false;
    },
    get queryParams(): URLSearchParams {
      return new URLSearchParams(self.location.search);
    },
    get isWorkspaceMain() {
      return self.params?.workspaceId;
    },
    get currentPage() {
      if (self.location.pathname.includes('/routes')) {
        return Pages.SCHEDULING;
      }
      if (self.location.pathname.includes('/drivers')) {
        return Pages.DRIVER_LISTING;
      }
      if (self.location.pathname.includes('/settings')) {
        return Pages.SETTINGS;
      }
      if (self.location.pathname.includes('/orders')) {
        return Pages.ORDERS;
      }
      if (self.location.pathname.includes('/insights')) {
        return Pages.INSIGHTS;
      }
      if (self.location.pathname.includes('/insights/details')) {
        return Pages.INSIGHT_DETAILS;
      }
      return Pages.NOT_FOUND;
    },
    get currentAppView(): Pages {
      if (this.isWorkspaceMain) {
        if (self.location.pathname.includes('/routes')) {
          return Pages.SCHEDULING;
        }
        if (self.location.pathname.includes('/drivers')) {
          return Pages.DRIVER_LISTING;
        }
        if (self.location.pathname.includes('/orders')) {
          return Pages.ORDERS;
        }
        if (self.location.pathname.includes('/customers')) {
          return Pages.CUSTOMERS;
        }
      }
      // Order matters here. From subpages to root level
      if (self.location.pathname.includes('/insights/details')) {
        return Pages.INSIGHT_DETAILS;
      }
      if (self.location.pathname.includes('/insights')) {
        return Pages.INSIGHTS;
      }
      if (self.location.pathname.includes('/settings')) {
        return Pages.SETTINGS;
      }
      return Pages.NOT_FOUND;
    },
  }))
  .actions((self) => ({
    replaceQueryParams(newParams: QueryParamsToReplace, replace?: boolean) {
      const params = self.queryParams;
      const previousOrder = params.get(SchedulingQueryParams.ORDER);
      const previousCustomer = params.get('customer');

      if (newParams) {
        Object.entries(newParams).forEach(([key, value]) => {
          if (value) {
            params.set(key, String(value));
          } else {
            params.delete(key);
          }
        });

        if (self.currentAppView === Pages.ORDERS && params.get(SchedulingQueryParams.ORDER) !== previousOrder && !('customer' in newParams)) {
          params.delete('customer');
        }
        if (self.currentAppView === Pages.CUSTOMERS && params.get('customer') !== previousCustomer && !('order' in newParams)) {
          params.delete(SchedulingQueryParams.ORDER);
        }

        const sortedParams = sortParams(self.currentAppView, params);
        const state = sortedParams.get(SchedulingQueryParams.ORDER) === previousOrder && sortedParams.get('customer') === previousCustomer
          ? self.location.state : undefined;

        if (sortedParams.toString() !== self.location?.search.toString()) {
          if (replace) {
            self.replace({ search: sortedParams.toString(), state });
          } else {
            self.push({ search: sortedParams.toString(), state });
          }
        }
      }
    },
    redirectToDate(date: string) {
      const selectedView = getSelectedView(TopMenuView.UNSCHEDULED, getAppRoot(self).ui.currentView);

      if (!DateTime.fromISO(date).isValid) {
        return;
      }

      this.replaceQueryParams({
        [SchedulingQueryParams.DATE]: date,
        [SchedulingQueryParams.VIEW]: selectedView,
      });
    },
    // Navigate to the routes page for a specific date. Used after scheduling
    // from the orders page so the user lands on the day their orders were
    // scheduled to. We set the Scheduled view explicitly: the orders were just
    // scheduled onto routes for this day, so that's where they show up. The view
    // MUST be committed here — the tab-matches-date reaction in App.ts only fires
    // on a data-signal change (date/hasRoutes/loading), so when the date is
    // carried over from the orders page it never runs and would leave the view
    // param absent, rendering no panel.
    redirectToRoutesPage(date?: string) {
      const workspaceId = self.params?.workspaceId;

      if (!workspaceId || !date || !DateTime.fromISO(date).isValid) {
        return;
      }

      self.push({
        pathname: routesPath.replace(':workspaceId', workspaceId),
        search: new URLSearchParams({
          [SchedulingQueryParams.DATE]: date,
          [SchedulingQueryParams.VIEW]: resolveStoredScheduledView(),
        }).toString(),
      });
    },
  }))
  .actions(onValidateParams)
  .actions(onWorkspaceIdParamChange);

export default Router;
export interface IRouterStore extends Instance<typeof Router> { }
